/**
 * Login Page
 *
 * Email/password login form with:
 * - Form validation
 * - Error handling
 * - Redirect after successful login
 */

import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../../context/AuthContext';
import { useOrganization } from '../../context/OrganizationContext';
import { getErrorMessage } from '../../api/client';
import GoogleSignInButton from '../../components/auth/GoogleSignInButton';
import Logo from '../../components/brand/Logo';
import { safeInternalPath } from '../../utils/safeRedirect';

// =============================================================================
// Validation Schema
// =============================================================================

const loginSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormData = z.infer<typeof loginSchema>;

// =============================================================================
// Login Page Component
// =============================================================================

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, loginWithGoogle, user } = useAuth();
  const { currentOrg, isLoading: orgLoading } = useOrganization();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Set when Google sign-in succeeds at Google but no ProgramRow account
  // exists for that email — renders a guidance screen instead of an error.
  const [googleNoAccount, setGoogleNoAccount] = useState<string | null>(null);

  // Get the page user was trying to access (for redirect after login)
  // Only internal paths survive (safeRedirect.ts: React Router 6 open-redirect guard).
  const from = safeInternalPath((location.state as { from?: { pathname: string } })?.from?.pathname);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  // If already logged in and org context is ready, redirect
  useEffect(() => {
    if (user && !orgLoading) {
      if (currentOrg) {
        // User has org - redirect based on org role
        const isCrewOnly = currentOrg.role === 'CREW';
        const redirectTo = from || (isCrewOnly ? '/crew' : '/admin');
        navigate(redirectTo, { replace: true });
      } else if (user.orgs && user.orgs.length === 0) {
        // User has no orgs (e.g., platform admin) - redirect to admin
        const redirectTo = from || '/admin';
        navigate(redirectTo, { replace: true });
      }
    }
  }, [user, orgLoading, currentOrg, from, navigate]);

  const onSubmit = async (data: LoginFormData) => {
    setError(null);
    setIsSubmitting(true);

    try {
      await login(data);

      // Redirect based on role or previous location
      // We need to get the user after login to check role
      // The auth context will update and trigger a re-render
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Google account exists, ProgramRow account doesn't — guide, don't strand.
  if (googleNoAccount) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper px-4">
        <div className="max-w-md w-full bg-white p-8 border border-hairline">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-primary-100 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 3a9 9 0 100 18 9 9 0 000-18z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No account yet for {googleNoAccount}</h2>
            <p className="text-gray-600 text-sm">
              You signed in with Google successfully, but there's no ProgramRow account under
              that email. Two ways forward:
            </p>
          </div>
          <div className="space-y-4">
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-1">Are you a crew member?</h3>
              <p className="text-sm text-gray-600">
                Ask your organization's administrator to add you. You'll get an email invitation,
                and after that you can sign in here with Google.
              </p>
            </div>
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-1">Setting up your own organization?</h3>
              <p className="text-sm text-gray-600 mb-3">
                Create it in a couple of minutes, and you can use this same Google account.
              </p>
              <Link
                to="/signup"
                className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 transition-colors"
              >
                Sign up your organization
              </Link>
            </div>
          </div>
          <p className="mt-6 text-center text-sm text-gray-500">
            <button
              type="button"
              onClick={() => setGoogleNoAccount(null)}
              className="text-primary-600 hover:text-primary-500 font-medium"
            >
              Back to sign in
            </button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-paper px-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="flex justify-center">
            <Logo size={22} on="light" wordmarkSize={30} />
          </h1>
          <p className="mt-3 text-ink-body">Sign in to your account</p>
        </div>

        {/* Login Form */}
        <div className="bg-white p-8 border border-hairline">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                {error}
              </div>
            )}

            {/* Email Field */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                {...register('email')}
                className={`
                  block w-full px-3 py-2 border rounded-md shadow-sm
                  focus:outline-none focus:ring-2 focus:ring-rack focus:border-rack
                  ${errors.email ? 'border-red-300' : 'border-gray-300'}
                `}
                placeholder="you@example.com"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Password
              </label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                {...register('password')}
                className={`
                  block w-full px-3 py-2 border rounded-md shadow-sm
                  focus:outline-none focus:ring-2 focus:ring-rack focus:border-rack
                  ${errors.password ? 'border-red-300' : 'border-gray-300'}
                `}
                placeholder="Enter your password"
              />
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.password.message}
                </p>
              )}
            </div>

            {/* Forgot Password Link */}
            <div className="flex justify-end">
              <Link
                to="/forgot-password"
                className="text-sm text-primary-600 hover:text-primary-500"
              >
                Forgot your password?
              </Link>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className={`
                w-full flex justify-center py-2.5 px-4 border border-transparent rounded-md
                shadow-sm text-sm font-medium text-white bg-primary-600
                hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2
                focus:ring-rack transition-colors
                ${isSubmitting ? 'opacity-75 cursor-not-allowed' : ''}
              `}
            >
              {isSubmitting ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Signing in...
                </>
              ) : (
                'Sign in'
              )}
            </button>
          </form>

          {/* Google Sign-In (renders nothing when VITE_GOOGLE_CLIENT_ID is unset).
              Redirect happens via the user-state effect above, same as password login. */}
          <GoogleSignInButton
            onCredential={async (idToken) => {
              setError(null);
              try {
                await loginWithGoogle(idToken);
              } catch (err) {
                const data = (err as { response?: { data?: { code?: string; email?: string } } })
                  .response?.data;
                if (data?.code === 'GOOGLE_NO_ACCOUNT') {
                  setGoogleNoAccount(data.email ?? 'your Google account');
                } else {
                  setError(getErrorMessage(err));
                }
              }
            }}
          />
        </div>

        {/* Legal links must be reachable from an unauthenticated page — A2P 10DLC
            vetting checks that they are, and users need them before signing up. */}
        <p className="mt-6 text-center text-xs text-gray-500">
          <Link to="/privacy" className="hover:text-gray-700 underline">
            Privacy Policy
          </Link>
          <span className="mx-2">·</span>
          <Link to="/terms" className="hover:text-gray-700 underline">
            Terms of Service
          </Link>
        </p>

      </div>
    </div>
  );
}
